import React, { useEffect, useState } from 'react';
import {
  ICalculatorValues,
  ISliderValues,
  IMultiSliderCalculator,
} from './types';
import { RichText } from 'prismic-reactjs';
import Question from '../../visuals/Icons/Old/Question';
import { PrismicLinkContext } from '@monofront/types';
import CustomPrismicLink from '../../prismic-link/CustomPrismicLink';
import Popover from '../../components/Popover';
import RangeWithLegend from './components/RangeWithLegend';
import ResultDisplay from './components/ResultDisplay';
import CalculatorCheatSheet from './components/CheatSheet';

/** Do not forget to import Popover Stylesheets 'tippy.js/dist/tippy.css' on the parent app when using MultiSliderCalculator */
export const MultiSliderCalculator: React.FC<IMultiSliderCalculator> = ({
  primary: contents,
  withCheatSheet = false,
  context = PrismicLinkContext.SHOP,
  isFrequencyVisible,
}) => {
  const [isCheatSheetDisplayed, setIsCheatSheetDisplayed] =
    useState(withCheatSheet);
  const [resultValue, setResultValue] = useState('');
  const [resultLegend, setResultLegendValue] = useState('');
  const [firstSliderPosition, setFirstSliderPosition] = useState(
    contents.firstSliderSelectedOption,
  );
  const [secondSliderPosition, setSecondSliderPosition] = useState(
    contents.secondSliderSelectedOption,
  );

  let calculatorValues: ICalculatorValues = null;
  let legendValues: ICalculatorValues = null;
  let firstSliderValues: ISliderValues | null = null;
  let secondSliderValues: ISliderValues | null = null;

  useEffect(() => {
    if (window.sessionStorage.getItem('LF_CALCULATOR_CHEATSHEET')) {
      setIsCheatSheetDisplayed(true);
    }
  }, []);

  useEffect(() => {
    const result =
      calculatorValues?.[firstSliderPosition][secondSliderPosition];
    const resultLegend =
      legendValues?.[firstSliderPosition][secondSliderPosition];

    if (result) {
      setResultValue(result);
    }
    if (resultLegend) {
      setResultLegendValue(resultLegend);
    }
  }, [firstSliderPosition, secondSliderPosition]);

  try {
    calculatorValues = JSON.parse(contents.calculatorValues);
    legendValues = contents.legendValues && JSON.parse(contents.legendValues);
    firstSliderValues = contents.firstSliderValues.split('|');
    secondSliderValues = contents.secondSliderValues.split('|');

    if (!calculatorValues || !calculatorValues.length) {
      throw Error('No values found');
    }
    if (
      !firstSliderValues ||
      !firstSliderValues.length ||
      firstSliderValues.length !== calculatorValues.length
    ) {
      throw Error(
        'The first slider should have the same number of entries as the number of line in the calculatorValues array.',
      );
    }
    const secondSliderHasCorrectLength = calculatorValues.every(
      (line) => line.length === secondSliderValues?.length,
    );

    if (!secondSliderHasCorrectLength) {
      throw Error(
        'The second slider should have the same number of entries as the number of options for each line of the calculatorValues array.',
      );
    }
  } catch (error: any) {
    console.error('Cannot build the calculator:', error);

    return (
      <p>
        Cannot build the calculator. Please check your data.
        <br />
        <i>{error.message}</i>
      </p>
    );
  }

  const sliderMinMaxValues = (items: ISliderValues): [number, number] => {
    return [0, items.length - 1];
  };

  const [firstSliderMin, firstSliderMax] =
    sliderMinMaxValues(firstSliderValues);
  const [secondSliderMin, secondSliderMax] =
    sliderMinMaxValues(secondSliderValues);

  return (
    <div className="root">
      {isCheatSheetDisplayed && (
        <CalculatorCheatSheet
          calculatorValues={calculatorValues}
          legendValues={legendValues}
          firstSliderText={contents.firstSliderText}
          secondSliderText={contents.secondSliderText}
          firstSliderValues={firstSliderValues}
          secondSliderValues={secondSliderValues}
        />
      )}
      <div className="title-container">
        <RichText render={contents.title} />

        {contents.tooltipText?.length > 0 && (
          <Popover
            content={
              <div className="popover">
                <RichText
                  render={contents.tooltipText}
                  serializeHyperlink={CustomPrismicLink(context)}
                />
              </div>
            }
          >
            <div className="tooltip-icon">
              <Question width={22} height={22} />
            </div>
          </Popover>
        )}
      </div>
      <div className="row tw-items-center">
        <div className="range tw-h-1/2">
          <RangeWithLegend
            min={firstSliderMin}
            max={firstSliderMax}
            legend={contents.firstSliderText}
            items={firstSliderValues}
            position={firstSliderPosition}
            setPosition={setFirstSliderPosition}
          />
          {isFrequencyVisible && (
            <RangeWithLegend
              min={secondSliderMin}
              max={secondSliderMax}
              legend={contents.secondSliderText}
              items={secondSliderValues}
              position={secondSliderPosition}
              setPosition={setSecondSliderPosition}
            />
          )}
        </div>
        <div className="result">
          <ResultDisplay
            image={contents.image}
            value={resultValue}
            resultLegend={resultLegend}
            numberLegend={contents.initialText}
            description={contents.totalPriceDescription}
            ctaLabel={contents.ctaLabel}
            ctaLink={contents.ctaLink}
            ctaText={contents.ctaText}
            context={context}
          />
        </div>
      </div>
      <style jsx>{`
        .root {
          margin-bottom: var(--space-8);
        }

        .root :global(strong) {
          color: var(--clr-secondary-1);
        }

        .popover > :global(p) {
          text-align: center;
          color: var(--white);
        }

        .title-container {
          display: flex;
          flex-direction: row;
          margin-bottom: var(--space-5);
        }

        .popover :global(p) {
          text-align: center;
          color: var(--white);
          font-size: var(--font-size-sm);
        }

        .row {
          display: flex;
        }

        .range {
          padding: var(--space-6) var(--space-8);
          background-color: var(--clr-tertiary-2);
          display: flex;
          flex-direction: column;
          justify-content: center;
          flex: 1.5;
          border-radius: var(--rd-4);
        }

        .range > :global(div:first-child) {
          margin-bottom: 80px;
        }

        .range > :global(div:last-child) {
          margin-bottom: var(--space-8);
        }

        .result {
          flex: 2;
          display: flex;
          flex-direction: column;
          justify-content: center;
          text-align: center;
          padding: var(--space-5) var(--padding-layout-horizontal-base);
          background-color: var(--white);
        }

        .popover :global(p) {
          color: var(--clr-black);
          text-align: left;
        }

        .tooltip-icon {
          margin-left: var(--space-1);
          margin-top: var(--negative-space-1);
        }

        @media (max-width: 800px) {
          .row {
            flex-direction: column;
          }

          .result {
            align-items: center;
            flex-direction: row;
          }

          .range {
            padding: var(--space-4) var(--space-5);
          }

          .range > :global(div:first-child) {
            margin-bottom: var(--space-8);
          }

          .title-container :global(h2) {
            all: unset;
            font-size: var(--font-size-2-5);
            font-weight: var(--font-weight-bold);
            font-family: var(--font-family-1);
          }
        }
      `}</style>
    </div>
  );
};

export default MultiSliderCalculator;
